import React, { useCallback, useEffect, useMemo } from 'react';
import { noop } from 'lodash';
import Icon from 'react-fontawesome';
import styled from 'styled-components';

import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  Row,
  SortingState,
  TableOptions,
  useReactTable,
} from '@tanstack/react-table';

import ColumnToggle from 'components/shared/ColumnToggle';
import { useColumnVisibility } from 'hooks/useColumnVisibility';

import { buildGridTemplateTracks } from './buildGridTemplateColumns';
import EmptyRow from './EmptyRow';
import LoadingOverlay from './LoadingOverlay';
import TablePaginator from './TablePaginator';
import { TablePaginatorProps } from './TablePaginator';

type TableProps<T> = Partial<TableOptions<T>> &
  Required<Pick<TableOptions<T>, 'columns' | 'data'>> & {
    paginated?: boolean;
    loading?: boolean;
    onSortChange?: (sorting: SortingState) => void;
    onPageChange?: (pageIndex: number) => void;
    onRowClick?: (row: Row<T>) => void;
    rowStyle?: (row: Row<T>) => React.CSSProperties | undefined;
    sorting: { sortBy: SortingState; setSortBy: (sort: SortingState) => void };
    showColumnToggle?: boolean;
    userColumnVisibility?: Record<string, boolean>;
    onColumnToggle?: (id: string) => void;
  };

export default function Table<T>({
  paginated = true,
  loading = false,
  onSortChange,
  onPageChange,
  onRowClick,
  rowStyle,
  sorting,
  showColumnToggle = false,
  userColumnVisibility: externalColumnVisibility,
  onColumnToggle: externalToggle,
  ...reactTableProps
}: TableProps<T>) {
  const local = useColumnVisibility();
  const userColumnVisibility = externalColumnVisibility ?? local.userColumnVisibility;
  const handleToggle = externalToggle ?? local.handleToggle;

  const parentVisibility = useMemo(
    () => reactTableProps.state?.columnVisibility || {},
    [reactTableProps.state?.columnVisibility]
  );

  // A column belongs in the toggle list only if it has a real string header.
  // This excludes utility columns like the row-actions menu, which should
  // always be shown and never appear as a checkbox.
  const isToggleableColumn = useCallback(
    (col: ColumnDef<T, any>): col is ColumnDef<T, any> & { id: string; header: string } =>
      typeof col.id === 'string' &&
      col.id.length > 0 &&
      typeof col.header === 'string' &&
      col.header.length > 0 &&
      parentVisibility[col.id] !== false,
    [parentVisibility]
  );

  // The first toggleable column is the primary column — it stays locked on so
  // users can't hide every column and lose track of which row is which.
  const lockedColumnId = useMemo(() => {
    if (!showColumnToggle) return undefined;
    return (reactTableProps.columns as ColumnDef<T, any>[]).find(isToggleableColumn)?.id;
  }, [reactTableProps.columns, isToggleableColumn, showColumnToggle]);

  const mergedColumnVisibility = useMemo(() => {
    if (!showColumnToggle) return parentVisibility;
    const result: Record<string, boolean> = { ...parentVisibility };
    for (const [id, userVisible] of Object.entries(userColumnVisibility)) {
      if (result[id] !== false) {
        result[id] = userVisible;
      }
    }
    if (lockedColumnId) {
      result[lockedColumnId] = true;
    }
    return result;
  }, [parentVisibility, userColumnVisibility, showColumnToggle, lockedColumnId]);

  const toggleableColumns = useMemo(() => {
    if (!showColumnToggle) return [];
    return (reactTableProps.columns as ColumnDef<T, any>[]).filter(isToggleableColumn).map((col) => ({
      id: col.id,
      label: col.header,
      visible: mergedColumnVisibility[col.id] !== false,
      locked: col.id === lockedColumnId,
    }));
  }, [reactTableProps.columns, isToggleableColumn, mergedColumnVisibility, showColumnToggle, lockedColumnId]);

  const table = useReactTable({
    ...reactTableProps,
    state: {
      ...reactTableProps.state,
      sorting: sorting.sortBy,
      columnVisibility: mergedColumnVisibility,
    },
    onSortingChange: (updater) => {
      if (updater instanceof Function) {
        sorting.setSortBy(updater(sorting.sortBy));
      }
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const paginatorProps: TablePaginatorProps = {
    canNextPage: table.getCanNextPage(),
    canPreviousPage: table.getCanPreviousPage(),
    gotoPage: table.setPageIndex,
    nextPage: table.nextPage,
    onPageChange: onPageChange || noop,
    previousPage: table.previousPage,
    pageCount: reactTableProps.pageCount || 0,
    pageIndex: table.getState().pagination.pageIndex,
  };

  const handlePageChange = () => {
    onPageChange?.(table.getState().pagination.pageIndex);
  };

  const handleSortChange = () => {
    onSortChange?.(table.getState().sorting);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(handlePageChange, [table.getState().pagination.pageIndex]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(handleSortChange, [table.getState().sorting]);

  const firstHeaderGroup = table.getHeaderGroups()[0];
  const lastHeader = firstHeaderGroup?.headers[firstHeaderGroup.headers.length - 1];
  const lastColHeader = lastHeader?.column.columnDef.header;
  // Columns without a string header (e.g. a row-actions menu column) are
  // treated as the anchor for the toggle icon rather than getting their
  // own extra cell appended.
  const lastColumnIsActions = !lastColHeader || typeof lastColHeader !== 'string';
  const showExtraToggleCell = showColumnToggle && !lastColumnIsActions;

  const gridTemplateColumns = [
    ...buildGridTemplateTracks(table.getVisibleLeafColumns()),
    ...(showExtraToggleCell ? ['minmax(50px, 50fr)'] : []),
  ].join(' ');

  return (
    <React.Fragment>
      <StyledTable $gridTemplateColumns={gridTemplateColumns}>
        <StyledTableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <StyledTableRow key={headerGroup.id}>
              {headerGroup.headers.map((header, index) => {
                const isToggleCell =
                  showColumnToggle && index === headerGroup.headers.length - 1 && lastColumnIsActions;
                // A column whose header is a render function (one that needs an icon or tooltip)
                // would otherwise stringify its whole source into the attribute; fall back to the
                // column id so the selector stays short and stable.
                const headerDef = header.column.columnDef.header;
                const headerLabel = typeof headerDef === 'string' ? headerDef : header.column.id;
                return (
                  <StyledTableHeaderCell
                    data-cy={`${headerLabel.toLowerCase()}-header-column`}
                    key={header.id}
                    isSorted={!!header.column.getIsSorted()}
                    canSort={header.column.getCanSort()}
                    aria-sort={ariaSort(header.column.getIsSorted(), header.column.getCanSort())}
                    style={isToggleCell ? { justifyContent: 'flex-end' } : undefined}
                    onClick={header.column.getToggleSortingHandler()}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {{
                      asc: <StyledIcon name={'caret-up'} size={'lg'} />,
                      desc: <StyledIcon name={'caret-down'} size={'lg'} />,
                    }[header.column.getIsSorted() as string] ?? null}
                    {isToggleCell && <ColumnToggle columns={toggleableColumns} onToggle={handleToggle} />}
                  </StyledTableHeaderCell>
                );
              })}
              {showExtraToggleCell && (
                <ColumnToggleHeaderCell isSorted={false} canSort={false}>
                  <ColumnToggle columns={toggleableColumns} onToggle={handleToggle} />
                </ColumnToggleHeaderCell>
              )}
            </StyledTableRow>
          ))}
        </StyledTableHeader>
        <StyledTableBody>
          {loading && <LoadingOverlay />}

          {!table.getRowModel().rows.length && <EmptyRow />}

          {!!table.getRowModel().rows.length &&
            table.getRowModel().rows.map((row) => (
              <StyledTableRow
                key={row.id}
                data-cy='tableRow'
                $clickable={!!onRowClick}
                style={rowStyle?.(row)}
                onClick={(event) => {
                  // Leave clicks meant for a control in the row (its ⋯ menu) to that control. The
                  // menu's items are portalled to document.body: React still bubbles their clicks
                  // here, but they aren't inside the row in the DOM. Don't stopPropagation in the
                  // menu instead: Menu closes on document clicks, so other open menus wouldn't.
                  const target = event.target as Element;
                  if (!event.currentTarget.contains(target) || target.closest('[data-row-click-ignore]')) return;
                  onRowClick?.(row);
                }}>
                {row.getVisibleCells().map((cell) => (
                  <StyledTableCell key={cell.id} {...cell.column.columnDef.meta}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </StyledTableCell>
                ))}
                {showExtraToggleCell && <StyledTableCell />}
              </StyledTableRow>
            ))}
        </StyledTableBody>
      </StyledTable>
      {paginated && <TablePaginator {...paginatorProps} />}
    </React.Fragment>
  );
}

function ariaSort(sorted: false | 'asc' | 'desc', canSort: boolean) {
  if (sorted) return sorted === 'asc' ? 'ascending' : 'descending';
  return canSort ? 'none' : undefined;
}

// A CSS grid of divs, so it states its table semantics itself; without them a screen reader can't
// announce a cell's column, and the roles must come as the full set or they are invalid ARIA.
const StyledTable = styled.div.attrs({ role: 'table' })<{ $gridTemplateColumns: string }>`
  overflow-x: auto;
  display: grid;
  grid-template-columns: ${({ $gridTemplateColumns }) => $gridTemplateColumns};
  font-size: 14px;
  padding-bottom: 30px;
  /* Set relative position so any absolute position elements (flyout menu) are relative to the table. */
  position: relative;

  .span-all-columns {
    grid-column: 1 / -1;
    display: flex;
    justify-content: center;
  }
`;

const StyledTableHeader = styled.div.attrs({ role: 'rowgroup' })`
  display: contents;
`;

const StyledIcon = styled(Icon)`
  padding-left: 8px;
  padding-top: 1px;
`;

const StyledTableBody = styled.div.attrs({ role: 'rowgroup' })`
  display: grid;
  grid-template-columns: subgrid;
  grid-column: 1 / -1;
  background-color: ${({ theme }) => theme.colors.white};
`;

export const StyledTableRow = styled.div.attrs({ role: 'row' })<{ $clickable?: boolean }>`
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  display: grid;
  grid-template-columns: subgrid;
  grid-column: 1 / -1;

  & > *:first-child {
    padding-left: ${({ theme }) => theme.dimensions.marginBig};
  }

  & > *:last-child {
    padding-right: ${({ theme }) => theme.dimensions.marginBig};
  }
`;

export const StyledTableCell = styled.div.attrs({ role: 'cell' })`
  align-items: center;
  background-color: inherit;
  border-bottom: 1px solid ${({ theme }) => theme.colors.black25};
  display: flex;
  min-width: 0;
  padding: 15px 5px;
  text-overflow: ellipsis;
  white-space: initial;
`;

const StyledTableHeaderCell = styled(StyledTableCell).attrs({ role: 'columnheader' })<{
  isSorted: boolean;
  canSort: boolean;
}>`
  align-items: normal;
  background-color: transparent;
  color: ${(props) => (props.isSorted ? props.theme.colors.black100 : props.theme.colors.black50)};
  font-weight: 500;
  cursor: ${(props) => (props.canSort ? 'pointer' : 'default')};
  user-select: none;
`;

const ColumnToggleHeaderCell = styled(StyledTableHeaderCell)`
  justify-content: flex-end;
  padding: 15px 5px;
`;
